import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosGet, axiosPost, axiosPut } from '../api/axios';
import { toast } from 'react-toastify';
import { TagExtended } from './useTags';
import { ExperiencePayloadData } from '../components/experience-review/experience-review';

export const EXPERIENCE_GRADE = {
  approved: 3,
  ungraded: 7,
  rejected: 10,
} as const;

export interface PipelineRunSummary {
  status: 'processing' | 'failed';
  run_id: number;
  current_step: string | null;
  error_message: string | null;
}

export interface ListenerAudio {
  admin_summary: string | null;
  ai_summary?: string;
  ai_title?: string;
  ai_indexed?: boolean;
  ai_processing_failed?: boolean;
  ai_audio_conversion_failed?: boolean;
  created_at: string;
  duration: number;
  excerpt: string | null;
  file_url: string;
  id: number;
  listener_role: ListenerRole;
  listener_role_id: number;
  media_type: 'AUDIO' | 'VIDEO' | null;
  message_quality: number;
  opensearch_tag_ids: number[];
  peer_title?: string | null;
  score?: number;
  title: string | null;
  topic_tag_ids: number[];
  tag_names?: string[];
  tag_group_names?: string[];
  total_favorites: number;
  total_playbacks: number;
  experience_counter?: number;
  favorite_count?: number;
  transcription: string | null;
  video_id: string | null;
  video_url?: string | null;
  profile_photo_square_file_url?: string | null;
  upload_source?: string | null;
  graded_by_user?: { email_address: string };
  graded_at?: string | null;
  user?: User;
  experience_tag?: { id: number; name: string };
  pipeline_run?: PipelineRunSummary | null;
}

export const getPipelineStatusLabel = (experience: Pick<ListenerAudio, 'pipeline_run'>) => {
  const status = experience.pipeline_run?.status;
  if (!status) return 'Complete';
  if (status === 'processing') return 'Processing';
  if (status === 'failed') return 'Failed';
  return status;
};

export const getPipelineStatusColor = (experience: Pick<ListenerAudio, 'pipeline_run'>) => {
  const status = experience.pipeline_run?.status;
  if (!status) return 'text-green';
  if (status === 'processing') return 'text-yellow-600';
  if (status === 'failed') return 'text-red';
  return 'text-gray-600';
};

export type UploadSource = 'UNKNOWN' | 'PWA_UPLOAD' | 'PWA_RECORD' | 'SB_AUDIO_UPLOAD' | 'SB_VIDEO_UPLOAD';

export interface ListenerRole {
  id: number;
  user: User;
  client_id?: number | null;
  client_name?: string | null;
  domain_id?: number | null;
  domain?: { id: number; key: string; name: string } | null;
}

export interface User {
  created_at: string;
  display_name: string;
  display_name_rejected: boolean;
  email_address: string;
  first_name: string;
  id: number;
  last_name: string;
  proposed_display_name: string;
  test_user: boolean;
}

export interface ListenerAudioResponse {
  count: number;
  data: ListenerAudio[];
}

export interface ListenerAudioBrowseResponse {
  count: number;
  data: ListenerAudio[];
  page?: number;
  limit?: number;
  total_pages?: number;
}

export interface ListenerAudioRequestParams {
  experience_type?: string;
  grade?: string;
  hide_incomplete_peers?: boolean;
  include_test_users?: boolean;
  limit?: number;
  order_by?: string;
  order_by_direction?: string;
  page?: number;
  search?: string;
}

export interface ListenerAudioBrowseParams {
  experience_type?: 'audio' | 'video';
  page?: number;
  limit?: number;
  search?: string;
  admin_override?: boolean;
  tag_groups?: number[];
  tag_ids?: number[];
  domain_id?: number;
  hide_incomplete_peers?: boolean;
  hide_test_users?: boolean;
  grade?: string;
  order_by?: 'created_at';
  order_direction?: 'asc' | 'desc';
}

const getAllListenersAudio = async (params: ListenerAudioRequestParams): Promise<ListenerAudioResponse> => {
  return await axiosGet('/listener_audio', { ...params }, 'v3').then(
    (listenerAudioResponse) => listenerAudioResponse.data,
  );
};

const getListenerAudioByListenerId = async (listenerId: number, params: ListenerAudioRequestParams) => {
  return await axiosGet(`/listener_audio/listener/${listenerId}`, { ...params }, 'v3').then(
    (listenerAudioResponse) => listenerAudioResponse.data,
  );
};

export const useAllListenerAudio = (params: ListenerAudioRequestParams) => {
  const { data, isLoading, error, refetch } = useQuery<ListenerAudioResponse>(['listener_audio', params], () =>
    getAllListenersAudio(params),
  );

  return { data, isLoading, error, refetch };
};

export const useListenerAudioByListenerId = (listenerId: number, params: ListenerAudioRequestParams) => {
  const { data, isLoading, error, refetch } = useQuery<ListenerAudioResponse>(
    ['listener_audio', listenerId, params],
    () => getListenerAudioByListenerId(listenerId, params),
  );

  return { data, isLoading, error, refetch };
};

const buildTagFilterParams = (tagGroups?: number[], tagIds?: number[]): Record<string, string> => {
  const params: Record<string, string> = {};
  if (tagGroups && tagGroups.length > 0) {
    params.tag_groups = tagGroups.join(',');
  }
  if (tagIds && tagIds.length > 0) {
    params.tag_ids = tagIds.join(',');
  }
  return params;
};

const browseListenerAudio = async (params: ListenerAudioBrowseParams): Promise<ListenerAudioBrowseResponse> => {
  const admin_override = params.admin_override ?? true;
  const searchTerm = params.search?.trim();

  // Check if search term is a numeric ID (listener_role_id search)
  const isNumericSearch = searchTerm && /^\d+$/.test(searchTerm);

  if (searchTerm && !isNumericSearch) {
    // Use Algolia for text-based searches
    const algoliaParams: any = {
      q: searchTerm,
      source_type: 'content',
      admin_override,
      ...(params.experience_type && { experience_type: params.experience_type }),
      ...buildTagFilterParams(params.tag_groups, params.tag_ids),
      ...(params.domain_id !== undefined && { domain_id: params.domain_id }),
    };

    const response = await axiosGet('/simple_search/algolia/search', algoliaParams, 'v3');
    const data = response.data;

    return {
      count: data.count || data.data?.length || 0,
      data: data.data || [],
      page: 1,
      limit: data.count || data.data?.length || 0,
      total_pages: 1,
    };
  }

  const requestParams: any = {
    page: params.page || 1,
    limit: params.limit || 20,
    admin_override,
    ...(params.experience_type && { experience_type: params.experience_type }),
    ...buildTagFilterParams(params.tag_groups, params.tag_ids),
    ...(params.domain_id !== undefined && { domain_id: params.domain_id }),
    ...(params.hide_incomplete_peers !== undefined && { hide_incomplete_peers: params.hide_incomplete_peers }),
    ...(params.hide_test_users !== undefined && { hide_test_users: params.hide_test_users }),
    ...(params.grade && { grade: params.grade }),
    // If search is a number, treat it as listener_role_ids filter (backend expects comma-separated string)
    ...(isNumericSearch && { listener_role_ids: searchTerm }),
    ...(params.order_by && { order_by: params.order_by }),
    ...(params.order_direction && { order_direction: params.order_direction }),
  };

  return await axiosGet('/listener_audio/browse', requestParams, 'v3').then((response) => response.data);
};

export const useBrowseListenerAudio = (params: ListenerAudioBrowseParams, options?: { enabled?: boolean }) => {
  const { data, isLoading, error, refetch } = useQuery<ListenerAudioBrowseResponse>(
    ['listener_audio_browse', params],
    () => browseListenerAudio(params),
    { enabled: options?.enabled ?? true },
  );

  return { data, isLoading, error, refetch };
};

export interface CreateExperienceRequest {
  audio_file_url?: string;
  video_file_url?: string;
  video_id?: string;
  title?: string;
  excerpt?: string;
  admin_summary?: string;
  message_quality?: string;
  topic_tag_ids?: number[];
  upload_source: UploadSource;
}

const createExperience = async (listenerId: number, payload: CreateExperienceRequest) => {
  const response = await axiosPost(`/listeners/${listenerId}/audio`, payload, 'v2');
  return response.data;
};

export const useCreateExperienceMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ listenerId, payload }: { listenerId: number; payload: CreateExperienceRequest }) => {
      return createExperience(listenerId, payload);
    },
    {
      onSuccess: () => {
        toast.success('Experience Created Successfully');
        queryClient.invalidateQueries(['listener_audio']);
      },
      onError: (error: any) => {
        try {
          const parsedError = JSON.parse(error);
          toast.error(parsedError?.data.description);
        } catch (e) {
          toast.error('Unknown error');
        }
      },
    },
  );
};

export interface UpdateVideoRequest {
  video_file_url?: string;
  title?: string;
  topic_tag_ids?: number[];
  upload_source?: UploadSource;
}

const uploadVideoFile = async (listenerId: number, audioId: number, payload: UpdateVideoRequest) => {
  const response = await axiosPut(`/listeners/${listenerId}/audio/${audioId}`, payload, 'v2');
  return response.data;
};

export const useUploadVideoFileMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ listenerId, audioId, payload }: { listenerId: number; audioId: number; payload: UpdateVideoRequest }) => {
      return uploadVideoFile(listenerId, audioId, payload);
    },
    {
      onSuccess: () => {
        toast.success('Video Uploaded Successfully');
        queryClient.invalidateQueries(['listener_audio']);
        queryClient.invalidateQueries(['listener_audio_browse']);
      },
      onError: (error: any) => {
        try {
          const parsedError = JSON.parse(error);
          toast.error(parsedError?.data?.description || 'Failed to upload video');
        } catch (e) {
          toast.error('Failed to upload video');
        }
      },
    },
  );
};

interface UpdateAudioRequest {
  topic_tag_ids?: number[];
  excerpt?: string;
  admin_summary?: string;
  upload_source: UploadSource;
}

const updateAudio = async (listenerId: number, audioId: number, payload: UpdateAudioRequest) => {
  const response = await axiosPut(`/listeners/${listenerId}/audio/${audioId}`, payload, 'v2');
  return response.data;
};

export const useUpdateAudioMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      listenerId,
      audioId,
      selectedExperience,
      experienceData,
      topicTags,
    }: {
      listenerId: number;
      audioId: number;
      selectedExperience: ListenerAudio;
      experienceData: ExperiencePayloadData;
      topicTags: TagExtended[];
    }) => {
      const selectedIds = topicTags.filter((tag: TagExtended) => tag.hasTag).map((tag: TagExtended) => tag.id);
      const MyStory = topicTags.find((tag: TagExtended) => tag.name === 'My Story' && tag.is_required);

      const payload = {
        topic_tag_ids: selectedExperience.title === 'My Story' && MyStory ? [...selectedIds, MyStory.id] : selectedIds,
        excerpt: experienceData.excerpt !== selectedExperience.excerpt ? experienceData.excerpt.trim() : undefined,
        admin_summary:
          experienceData.admin_summary !== selectedExperience.admin_summary
            ? experienceData.admin_summary.trim()
            : undefined,
        transcription:
          experienceData.transcription !== selectedExperience.transcription
            ? experienceData.transcription.trim()
            : undefined,
        video_id: experienceData.video_id !== selectedExperience.video_id ? experienceData.video_id : undefined,
      };

      return updateAudio(listenerId, audioId, payload);
    },
    {
      onSuccess: () => {
        toast.success('Audio Updated Successfully');
        queryClient.invalidateQueries(['listener_audio']);
      },
      onError: (error: any) => {
        try {
          const parsedError = JSON.parse(error);
          toast.error(parsedError?.data.description);
        } catch (e) {
          toast.error('Unknown error');
        }
      },
    },
  );
};

interface UpdateListenerAudioTagsPayload {
  topic_tag_ids?: number[];
  title?: string;
  excerpt?: string;
  admin_summary?: string;
  ai_title?: string;
  ai_summary?: string;
  transcription?: string;
  video_id?: string | null;
  file_path?: string;
  upload_source?: UploadSource;
}

const updateListenerAudio = async (listenerId: number, audioId: number, payload: UpdateListenerAudioTagsPayload) => {
  const response = await axiosPut(`/listeners/${listenerId}/audio/${audioId}`, payload, 'v2');
  return response.data;
};

export const useUpdateListenerAudio = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      listenerId,
      audioId,
      payload,
    }: {
      listenerId: number;
      audioId: number;
      payload: UpdateListenerAudioTagsPayload;
    }) => updateListenerAudio(listenerId, audioId, payload),
    {
      onMutate: async ({ audioId, payload }) => {
        await queryClient.cancelQueries(['listener_audio_browse']);
        await queryClient.cancelQueries(['listener_audio']);

        const updateAudio = (audio: ListenerAudio) => {
          if (audio.id !== audioId) return audio;
          return {
            ...audio,
            topic_tag_ids: payload.topic_tag_ids ?? audio.topic_tag_ids,
            tag_names: payload.topic_tag_ids !== undefined ? undefined : audio.tag_names,
            ...(payload.title !== undefined && { peer_title: payload.title, title: payload.title }),
            ...(payload.ai_title !== undefined && { ai_title: payload.ai_title }),
            ...(payload.ai_summary !== undefined && { ai_summary: payload.ai_summary }),
            ...(payload.transcription !== undefined && { transcription: payload.transcription }),
            ...(payload.excerpt !== undefined && { excerpt: payload.excerpt }),
            ...(payload.admin_summary !== undefined && { admin_summary: payload.admin_summary }),
            ...(payload.video_id !== undefined && { video_id: payload.video_id }),
          };
        };

        queryClient.setQueriesData<ListenerAudioBrowseResponse>(['listener_audio_browse'], (oldData) => {
          if (!oldData?.data) return oldData;
          return { ...oldData, data: oldData.data.map(updateAudio) };
        });

        queryClient.setQueriesData<ListenerAudioResponse>(['listener_audio'], (oldData) => {
          if (!oldData?.data) return oldData;
          return { ...oldData, data: oldData.data.map(updateAudio) };
        });
      },
      onSuccess: () => {
        toast.success('Content updated successfully');
        // Invalidate all listener_audio queries (including browse queries)
        queryClient.invalidateQueries(['listener_audio']);
        queryClient.invalidateQueries(['listener_audio_browse']);
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.description || 'Unknown error';
        toast.error(errorMessage);
        queryClient.invalidateQueries(['listener_audio_browse']);
        queryClient.invalidateQueries(['listener_audio']);
      },
    },
  );
};

interface GradeAudioRequest {
  system_message_id?: number;
  message?: string;
  message_quality: string;
}

const gradeAudio = async (listenerId: number, audioId: number, payload: GradeAudioRequest) => {
  const response = await axiosPost(`/listeners/${listenerId}/audio/${audioId}/grade`, payload, 'v2');
  return response.data;
};

export const useGradeAudioMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      listenerId,
      audioId,
      experienceData,
      experienceType,
    }: {
      listenerId: number;
      audioId: number;
      experienceData: ExperiencePayloadData;
      experienceType?: 'audio' | 'video';
    }) => {
      const sendMessage = !!(experienceData.messageId !== 'none' && experienceData.grade !== 'approved');
      const payload = {
        system_message_id: sendMessage ? +experienceData.messageId : undefined,
        message: sendMessage ? experienceData.message.trim() : undefined,
        message_quality: experienceData.grade,
      };

      return gradeAudio(listenerId, audioId, payload);
    },
    {
      onSuccess: (_data, variables) => {
        const label = variables.experienceType === 'video' ? 'Video' : 'Audio';
        toast.success(`${label} Grade Submitted Successfully`);
        queryClient.invalidateQueries(['listener_audio']);
      },
      onError: (error: any) => {
        try {
          const parsedError = JSON.parse(error);
          toast.error(parsedError?.data.description);
        } catch (e) {
          toast.error('Unknown error');
        }
      },
    },
  );
};
